import React from 'react';
import { Icon, Modal } from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';

interface RejectionStatusDetails {
  rejectionReason: string | null;
  nextAction: string | null;
}

interface RejectionModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Called when modal is closed */
  onClose: () => void;
  /** Status details containing rejection reason and reviewer comments */
  statusDetails: RejectionStatusDetails | null;
  /** Fallback title when rejectionReason is null */
  fallbackTitle?: string;
  /** Test ID for QA automation */
  testID?: string;
}

/**
 * Generic Rejection Modal
 *
 * Displays rejection reason and reviewer comments for any workflow (KYB, NDA, etc.)
 * that has a REJECTED status. Requires user acknowledgment before proceeding.
 */
export const RejectionModal: React.FC<RejectionModalProps> = ({
  isOpen,
  onClose,
  statusDetails,
  fallbackTitle,
  testID = 'rejection-modal',
}) => {
  const { t } = useTranslation('portal');
  const resolvedFallbackTitle =
    fallbackTitle ?? t('rejectionModal.updatesRequired');
  if (!statusDetails) return null;

  const { rejectionReason, nextAction } = statusDetails;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      testID={testID}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      className="focus-none"
    >
      <Modal.Header
        type="left"
        title={nextAction ? t(nextAction) : resolvedFallbackTitle}
        description={rejectionReason ?? ''}
        descriptionClassName="max-h-60 overflow-auto"
        icon={
          <div className="w-12 h-12 rounded-full bg-brand-green-500 flex items-center justify-center">
            <Icon name="Info" className="text-white" strokeWidth={1} />
          </div>
        }
        showCloseButton={true}
        testID={`${testID}-header`}
      />
      <Modal.Footer
        divider
        alignment="right"
        actions={[
          {
            label: t('rejectionModal.review'),
            variant: 'secondary',
            onClick: onClose,
            ctaIcon: 'Eye',
            testID: `${testID}-review-button`,
          },
        ]}
        testID={`${testID}-footer`}
      />
    </Modal>
  );
};
